/**
 * Central API client for Noureddine Koki Website
 * Coordinates fetch requests to the PHP REST Backend
 */

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    
    // Check if we are running on local Vite dev server
    if (window.location.port === '3000' || window.location.port === '5173') {
      return 'http://localhost/NOUR%20WEB/Backend_v1';
    }
    
    // If running under a nested folder structure like XAMPP /NOUR%20WEB/
    if (pathname.includes('/NOUR%20WEB/')) {
      return `${origin}/NOUR%20WEB/Backend_v1`;
    }
    
    // Production on Hostinger where the folder is named 'api'
    return `${origin}/api`;
  }
  return 'http://localhost/NOUR%20WEB/Backend_v1';
};

export const API_BASE_URL = getApiBaseUrl();

async function request(path: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}/${path.replace(/^\//, '')}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

// Admin auth
export async function loginAdmin(data: any): Promise<any> {
  return request('admin/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Media Upload
export async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);
  
  const response = await fetch(`${API_BASE_URL}/upload`, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) {
    const text = await response.text();
    let errMsg = `Error ${response.status}`;
    try {
      const json = JSON.parse(text);
      if (json.error) errMsg = json.error;
    } catch {
      errMsg = `Error ${response.status}: ${text.slice(0, 60)}...`;
    }
    throw new Error(errMsg);
  }
  
  const data = await response.json();
  return data.imageUrl; // Returns the file path e.g. /api/uploads/xxx.jpg
}

// Dashboard statistics
export async function fetchStats(): Promise<any> {
  return request('stats');
}

// Articles
export async function fetchArticles(params?: any): Promise<any> {
  let query = '';
  if (params) {
    const searchParams = new URLSearchParams(params);
    query = `?${searchParams.toString()}`;
  }
  return request(`articles${query}`);
}

export async function fetchArticle(slugOrId: number | string): Promise<any> {
  return request(`articles/${slugOrId}`);
}

export async function createArticle(data: any): Promise<any> {
  return request('articles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateArticle(id: number | string, data: any): Promise<any> {
  return request(`articles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteArticle(id: number | string): Promise<any> {
  return request(`articles/${id}`, {
    method: 'DELETE',
  });
}

// Writers
export async function fetchWriters(): Promise<any> {
  return request('writers');
}

export async function fetchWriter(id: number | string): Promise<any> {
  return request(`writers/${id}`);
}

export async function createWriter(data: any): Promise<any> {
  return request('writers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWriter(id: number | string, data: any): Promise<any> {
  return request(`writers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWriter(id: number | string): Promise<any> {
  return request(`writers/${id}`, {
    method: 'DELETE',
  });
}

// Videos
export async function fetchVideos(): Promise<any> {
  return request('videos');
}

export async function createVideo(data: any): Promise<any> {
  return request('videos', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateVideo(id: number | string, data: any): Promise<any> {
  return request(`videos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteVideo(id: number | string): Promise<any> {
  return request(`videos/${id}`, {
    method: 'DELETE',
  });
}

// Comments
export async function fetchComments(params?: any): Promise<any> {
  let query = '';
  if (params) {
    const searchParams = new URLSearchParams(params);
    query = `?${searchParams.toString()}`;
  }
  return request(`comments${query}`);
}

export async function createComment(data: any): Promise<any> {
  return request('comments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateComment(id: number | string, data: any): Promise<any> {
  return request(`comments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteComment(id: number | string): Promise<any> {
  return request(`comments/${id}`, {
    method: 'DELETE',
  });
}

// Polls
export async function fetchPoll(): Promise<any> {
  return request('polls');
}

export async function createPoll(data: any): Promise<any> {
  return request('polls', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function votePoll(pollId: number | string, optionIndex: number): Promise<any> {
  return request('polls/vote', {
    method: 'POST',
    body: JSON.stringify({ poll_id: pollId, option_index: optionIndex }),
  });
}

// Categories
export async function fetchCategories(): Promise<any> {
  return request('categories');
}

export async function createCategory(data: any): Promise<any> {
  return request('categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(id: number | string, data: any): Promise<any> {
  return request(`categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(id: number | string): Promise<any> {
  return request(`categories/${id}`, {
    method: 'DELETE',
  });
}

// Settings
export async function fetchSettings(): Promise<any> {
  return request('settings');
}

export async function updateSettings(data: any): Promise<any> {
  return request('settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Navigation Links
export async function fetchLinks(location?: string): Promise<any> {
  let query = '';
  if (location) {
    query = `?location=${location}`;
  }
  return request(`links${query}`);
}

export async function createLink(data: any): Promise<any> {
  return request('links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateLink(id: number | string, data: any): Promise<any> {
  return request(`links/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLink(id: number | string): Promise<any> {
  return request(`links/${id}`, {
    method: 'DELETE',
  });
}

// Home Sections Layout order
export async function fetchSections(): Promise<any> {
  return request('sections');
}

export async function updateSection(id: number | string, data: any): Promise<any> {
  return request(`sections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Hero Slides
export async function fetchHeros(): Promise<any> {
  return request('heros');
}

export async function createHero(data: any): Promise<any> {
  return request('heros', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateHero(id: number | string, data: any): Promise<any> {
  return request(`heros/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteHero(id: number | string): Promise<any> {
  return request(`heros/${id}`, {
    method: 'DELETE',
  });
}

// Static Pages content
export async function fetchPages(): Promise<any> {
  return request('pages');
}
export async function createPage(pageData: any): Promise<any> {
  return request('pages', { method: 'POST', body: JSON.stringify(pageData) });
}
export async function updatePage(id: number, pageData: any): Promise<any> {
  return request(`pages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(pageData),
  });
}

// Breaking News helper (queries latest articles)
export async function fetchBreakingNews(lang: string = 'ar'): Promise<string[]> {
  try {
    const articles = await fetchArticles({ limit: 5 });
    if (Array.isArray(articles) && articles.length > 0) {
      return articles.map(art => lang === 'ar' ? art.title : (art.title_en || art.title));
    }
  } catch (e) {
    console.error("fetchBreakingNews failed, using local defaults", e);
  }
  return [];
}
